import React, { useState, useEffect, useRef } from 'react';

const CompanionAnswerButton = ({ text, onClick, style }) => {
    const [isSelected, setIsSelected] = useState(false);
    const buttonRef = useRef(null);

    // Helper to split long text into two rows, each <= 32 chars, not breaking words if possible.
    // If a word must be split, add a hyphen at the end of the first row.
    // If text would require more than 2 rows (i.e., >64 chars), show error.
    function splitTextRows(str) {
        const trimmed = str.trim();
        if (trimmed.length <= 32) return ' ' + trimmed;
        if (trimmed.length > 64) return ' [Error: Too long]';

        // Try to split into two rows, each <= 32 chars, not breaking words if possible
        let firstRow = '';
        let secondRow = '';
        const words = trimmed.split(/\s+/);
        for (let i = 0; i < words.length; i++) {
            if ((firstRow + (firstRow ? ' ' : '') + words[i]).length <= 32) {
                firstRow += (firstRow ? ' ' : '') + words[i];
            } else {
                // If the word itself is longer than 32 chars, split it with a hyphen
                if (firstRow === '') {
                    const part1 = words[i].slice(0, 31) + '-';
                    const part2 = words[i].slice(31);
                    firstRow = part1;
                    secondRow = part2 + (words.length > i + 1 ? ' ' + words.slice(i + 1).join(' ') : '');
                } else {
                    secondRow = words.slice(i).join(' ');
                }
                break;
            }
        }
        // If secondRow is still too long, split with a hyphen (but only once, never more than 2 rows)
        if (secondRow.length > 32) {
            // Try to split at a space, otherwise split the word with a hyphen
            let splitIdx = secondRow.lastIndexOf(' ', 32);
            if (splitIdx === -1 || splitIdx < 16) splitIdx = 32;
            if (splitIdx < secondRow.length && secondRow[splitIdx] !== ' ') {
                // Split a long word with a hyphen
                secondRow = secondRow.slice(0, splitIdx - 1) + '-\n' + secondRow.slice(splitIdx - 1);
            } else {
                // Just show error if it would go to 3 rows
                return ' [Error: Too long]';
            }
        }
        // Only allow one leading space at the start of the button, none at the end
        return ' ' + firstRow.trimEnd() + (secondRow ? '\n' + secondRow.trimEnd() : '');
    }

    let displayText = splitTextRows(text);

    // For short answers, prevent wrapping and let button fit text
    // For long answers, allow wrapping with whiteSpace: 'pre-line'
    const isMultiRow = displayText.includes('\n');
    function getTextWidth(txt) {
        if (typeof window === 'undefined') return 90;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.font = '15px Times New Roman, Times, serif';
        // Only measure the first row for short answers
        const row = txt.split('\n')[0];
        return ctx.measureText(row).width + 16;
    }
    const width = isMultiRow ? undefined : Math.ceil(getTextWidth(displayText));

    useEffect(() => {
        if (buttonRef.current && !isMultiRow) {
            buttonRef.current.style.width = width ? `${width}px` : '';
            buttonRef.current.style.maxWidth = '';
            buttonRef.current.style.whiteSpace = 'nowrap';
        } else if (buttonRef.current && isMultiRow) {
            buttonRef.current.style.width = '';
            buttonRef.current.style.maxWidth = '';
            buttonRef.current.style.whiteSpace = 'pre-line';
        }
    }, [text, width, isMultiRow]);

    const handleClick = () => {
        setIsSelected(true);
        onClick();
    };

    return (
        <button
            ref={buttonRef}
            onClick={handleClick}
            style={{
                display: 'inline-block',
                backgroundColor: 'white',
                backgroundImage: 'none',
                color: 'black',
                padding: '0 8px',
                borderRadius: '5px',
                border: 'none',
                textAlign: 'center',
                cursor: 'pointer',
                fontWeight: isSelected ? 'bold' : 'normal',
                boxShadow: '3px 3px 5px rgba(0, 0, 0, 0.2)',
                margin: '0',
                verticalAlign: 'middle',
                lineHeight: '1.2',
                height: 'auto',
                boxSizing: 'border-box',
                // whiteSpace and width are handled by useEffect for best browser compatibility
                wordBreak: 'break-word',
                minWidth: isMultiRow ? '90px' : undefined,
                maxWidth: isMultiRow ? undefined : undefined,
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '15px',
                ...style,
            }}
            onMouseEnter={(e) => (e.target.style.fontWeight = 'bold')}
            onMouseLeave={(e) => {
                if (!isSelected) e.target.style.fontWeight = 'normal';
            }}
        >
            {displayText}
        </button>
    );
};

export default CompanionAnswerButton;
